import { useState, useMemo, useRef } from 'react'
import { useFinance } from '../../context/FinanceContext'
import { useSubscription } from '../../context/SubscriptionContext'
import { getThemeColors } from '../../lib/constants'
import { formatCurrency } from '../../lib/formatters'
import { STATE_TAX_DATA } from '../../lib/calculations'
import { calculateFreelancerRate } from '../../lib/calculations/freelancerRate'
import GlassCard from '../ui/GlassCard'
import Button from '../ui/Button'
import { trackExportReport } from '../../lib/analytics'

function InputField({ label, value, onChange, prefix, suffix, hint, children }) {
  if (children) {
    return (
      <div>
        <label className="block text-xs font-medium text-muted mb-1.5">{label}</label>
        {children}
        {hint && <p className="text-xs text-faint mt-1">{hint}</p>}
      </div>
    )
  }
  return (
    <div>
      <label className="block text-xs font-medium text-muted mb-1.5">{label}</label>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-faint text-sm">{prefix}</span>}
        <input
          type="number"
          value={value}
          onChange={e => onChange(parseFloat(e.target.value) || 0)}
          className={`w-full bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:border-primary transition ${prefix ? 'pl-7' : 'pl-3'} ${suffix ? 'pr-12' : 'pr-3'}`}
          min={0}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-faint text-xs">{suffix}</span>}
      </div>
      {hint && <p className="text-xs text-faint mt-1">{hint}</p>}
    </div>
  )
}

function ResultCard({ label, value, color, highlight, sub }) {
  return (
    <GlassCard className={`!p-4 ${highlight ? 'ring-1' : ''}`} style={highlight ? { borderColor: color + '44' } : {}}>
      <p className="text-xs text-muted mb-1">{label}</p>
      <p className={`font-bold ${highlight ? 'text-xl' : 'text-lg'}`} style={{ color }}>{value}</p>
      {sub && <p className="text-xs text-faint mt-0.5">{sub}</p>}
    </GlassCard>
  )
}

const FILING_STATUSES = [
  { key: 'single', label: 'Single' },
  { key: 'mfj', label: 'Married Filing Jointly' },
  { key: 'hoh', label: 'Head of Household' },
]

export default function FreelancerRate() {
  const { state } = useFinance()
  const { isPro } = useSubscription()
  const colors = getThemeColors(state.theme)

  const [targetW2Salary, setTargetW2Salary] = useState(100000)
  const [weeksPTO, setWeeksPTO] = useState(3)
  const [billableHoursPerWeek, setBillableHoursPerWeek] = useState(30)
  const [healthInsuranceCostPerMonth, setHealthInsuranceCostPerMonth] = useState(500)
  const [filingStatus, setFilingStatus] = useState('single')
  const [stateCode, setStateCode] = useState('OH')

  const result = useMemo(() => calculateFreelancerRate({
    targetW2Salary,
    weeksPTO,
    billableHoursPerWeek,
    healthInsuranceCostPerMonth,
    filingStatus,
    stateCode,
  }), [targetW2Salary, weeksPTO, billableHoursPerWeek, healthInsuranceCostPerMonth, filingStatus, stateCode])

  const handleExportPDF = async () => {
    trackExportReport()
    const { default: jsPDF } = await import('jspdf')
    const { default: autoTable } = await import('jspdf-autotable')

    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const margin = 14

    // --- Helper: check if we need a new page ---
    const ensureSpace = (needed, currentY) => {
      if (currentY + needed > pageHeight - margin) {
        doc.addPage()
        return margin + 4
      }
      return currentY
    }

    // --- Title & date ---
    doc.setFillColor(255, 255, 255)
    doc.rect(0, 0, pageWidth, pageHeight, 'F')

    doc.setFontSize(20)
    doc.setTextColor(30, 30, 30)
    doc.text('Freelancer Rate Calculator', margin, 22)
    doc.setFontSize(10)
    doc.setTextColor(128, 128, 128)
    doc.text(`Generated by Liquid Finance  \u2022  ${new Date().toLocaleDateString()}`, margin, 30)

    let y = 38

    // --- Section 1: Input Parameters ---
    doc.setFontSize(13)
    doc.setTextColor(50, 50, 50)
    doc.text('Input Parameters', margin, y)
    y += 4

    const filingLabel = FILING_STATUSES.find(f => f.key === filingStatus)?.label || filingStatus
    const stateEntry = STATE_TAX_DATA.find(s => s.code === stateCode)
    const stateLabel = stateEntry ? `${stateEntry.name}${stateEntry.rate !== null ? ` (${stateEntry.rate}%)` : ''}` : stateCode

    autoTable(doc, {
      startY: y,
      head: [['Parameter', 'Value']],
      body: [
        ['Target W-2 Salary Equivalent', `${formatCurrency(targetW2Salary)}/year`],
        ['Weeks of PTO Desired', `${weeksPTO} weeks`],
        ['Billable Hours per Week', `${billableHoursPerWeek} hrs/wk`],
        ['Health Insurance Cost', `${formatCurrency(healthInsuranceCostPerMonth)}/month`],
        ['Filing Status', filingLabel],
        ['State', stateLabel],
      ],
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: [40, 40, 40] },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: margin, right: margin },
    })
    y = doc.lastAutoTable.finalY + 10

    // --- Section 2: Result Cards ---
    y = ensureSpace(30, y)
    doc.setFontSize(13)
    doc.setTextColor(50, 50, 50)
    doc.text('Key Results', margin, y)
    y += 4

    autoTable(doc, {
      startY: y,
      head: [['Metric', 'Value', 'Note']],
      body: [
        ['Required Hourly Rate', formatCurrency(result.hourlyRate), 'Minimum billing rate'],
        ['Effective Daily Rate', formatCurrency(result.dailyRate), '8-hour day'],
        ['Annual Gross Needed', formatCurrency(result.annualGrossNeeded), `${result.totalBillableHours} billable hrs`],
        ['SE Tax Overhead', formatCurrency(result.seTaxOverhead), 'Extra vs W-2 FICA'],
      ],
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: [40, 40, 40] },
      columnStyles: { 2: { textColor: [120, 120, 120], fontStyle: 'italic' } },
      margin: { left: margin, right: margin },
    })
    y = doc.lastAutoTable.finalY + 10

    // --- Section 3: W-2 Employee vs Freelancer Comparison ---
    y = ensureSpace(50, y)
    doc.setFontSize(13)
    doc.setTextColor(50, 50, 50)
    doc.text('W-2 Employee vs Freelancer', margin, y)
    y += 4

    autoTable(doc, {
      startY: y,
      head: [['Category', 'W-2 Employee', 'Freelancer']],
      body: [
        ['Gross Income', formatCurrency(targetW2Salary), formatCurrency(result.annualGrossNeeded)],
        ['Total Taxes', formatCurrency(result.w2TotalTax), formatCurrency(result.freelancerTotalTax)],
        ['Health Insurance', 'Employer-provided', `${formatCurrency(result.annualHealthInsurance)}/yr`],
        ['PTO', 'Paid', `Unpaid (${weeksPTO} wks)`],
        ['Take-Home', formatCurrency(result.w2TakeHome), formatCurrency(result.freelancerTakeHome)],
      ],
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: [40, 40, 40] },
      didParseCell: (data) => {
        // Bold the Take-Home row
        if (data.row.index === 4) {
          data.cell.styles.fontStyle = 'bold'
        }
      },
      margin: { left: margin, right: margin },
    })
    y = doc.lastAutoTable.finalY + 10

    // --- Section 4: Waterfall Breakdown ---
    y = ensureSpace(60, y)
    doc.setFontSize(13)
    doc.setTextColor(50, 50, 50)
    doc.text('From W-2 Take-Home to Required Billing', margin, y)
    y += 4

    const waterfallBody = result.waterfall.map((item, i) => [
      item.label,
      `${i === 0 ? '' : '+'}${formatCurrency(item.amount)}`,
    ])
    // Add the total row
    waterfallBody.push(['Required Annual Billing', formatCurrency(result.annualGrossNeeded)])

    autoTable(doc, {
      startY: y,
      head: [['Component', 'Amount']],
      body: waterfallBody,
      theme: 'striped',
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: [40, 40, 40] },
      didParseCell: (data) => {
        // Highlight the final total row
        if (data.section === 'body' && data.row.index === waterfallBody.length - 1) {
          data.cell.styles.fontStyle = 'bold'
          data.cell.styles.fillColor = [219, 234, 254]
        }
        // Highlight first row (base)
        if (data.section === 'body' && data.row.index === 0) {
          data.cell.styles.fillColor = [220, 252, 231]
        }
      },
      margin: { left: margin, right: margin },
    })
    y = doc.lastAutoTable.finalY + 10

    // --- Section 5: Working Time Details ---
    y = ensureSpace(30, y)
    doc.setFontSize(13)
    doc.setTextColor(50, 50, 50)
    doc.text('Working Time', margin, y)
    y += 4

    autoTable(doc, {
      startY: y,
      head: [['Detail', 'Value']],
      body: [
        ['Working Weeks per Year', `${result.workingWeeks} weeks`],
        ['Billable Hours per Year', `${result.totalBillableHours} hours`],
        ['Billable Hours per Week', `${billableHoursPerWeek} hours`],
        ['PTO Weeks', `${weeksPTO} weeks`],
      ],
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: [40, 40, 40] },
      margin: { left: margin, right: margin },
    })
    y = doc.lastAutoTable.finalY + 12

    // --- Section 6: Freelancer Tip ---
    y = ensureSpace(30, y)
    doc.setFontSize(13)
    doc.setTextColor(50, 50, 50)
    doc.text('Freelancer Tip', margin, y)
    y += 6
    doc.setFontSize(9)
    doc.setTextColor(80, 80, 80)
    const tipText = `This rate ensures you match your W-2 take-home pay. In practice, consider adding 10-20% for: business expenses, accounting fees, liability insurance, equipment, retirement contributions (SEP-IRA/Solo 401k), and a buffer for slow months. Your effective rate should be closer to ${formatCurrency(result.hourlyRate * 1.15)}/hr.`
    const tipLines = doc.splitTextToSize(tipText, pageWidth - margin * 2)
    doc.text(tipLines, margin, y)

    doc.save(`freelancer-rate-${Date.now()}.pdf`)
  }

  return (
    <div className="space-y-6">
      {/* Inputs */}
      <GlassCard>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h3 className="text-lg font-semibold text-heading">Freelancer Rate Calculator</h3>
          <Button variant="secondary" size="sm" onClick={handleExportPDF} disabled={!isPro} title={!isPro ? 'Pro feature' : ''}>
            Export PDF{!isPro ? ' (Pro)' : ''}
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <InputField label="Target W-2 Salary Equivalent" value={targetW2Salary} onChange={setTargetW2Salary} prefix="$" suffix="/year" hint="What you'd earn as an employee" />
          <InputField label="Weeks of PTO Desired" value={weeksPTO} onChange={setWeeksPTO} suffix="weeks" hint="Vacation + sick days" />
          <InputField label="Billable Hours per Week" value={billableHoursPerWeek} onChange={setBillableHoursPerWeek} suffix="hrs/wk" hint="Time actually billing clients" />
          <InputField label="Health Insurance Cost" value={healthInsuranceCostPerMonth} onChange={setHealthInsuranceCostPerMonth} prefix="$" suffix="/month" />
          <InputField label="Filing Status">
            <div className="flex gap-1">
              {FILING_STATUSES.map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setFilingStatus(opt.key)}
                  className={`flex-1 py-2.5 rounded-xl text-xs font-medium transition-all ${
                    filingStatus === opt.key
                      ? 'bg-primary text-on-primary'
                      : 'bg-subtle text-muted hover:text-heading'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </InputField>
          <InputField label="State">
            <select
              value={stateCode}
              onChange={e => setStateCode(e.target.value)}
              className="w-full bg-input border border-input rounded-xl py-2.5 px-3 text-sm text-heading focus:outline-none focus:border-primary transition appearance-none"
            >
              {STATE_TAX_DATA.map(s => (
                <option key={s.code} value={s.code}>
                  {s.name}{s.rate !== null ? ` (${s.rate}%)` : ''}
                </option>
              ))}
            </select>
          </InputField>
        </div>
      </GlassCard>

      {/* Result Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <ResultCard
          label="Required Hourly Rate"
          value={formatCurrency(result.hourlyRate)}
          color={colors.primary}
          highlight
          sub="Minimum billing rate"
        />
        <ResultCard
          label="Effective Daily Rate"
          value={formatCurrency(result.dailyRate)}
          color={colors.secondary}
          sub="8-hour day"
        />
        <ResultCard
          label="Annual Gross Needed"
          value={formatCurrency(result.annualGrossNeeded)}
          color={colors.warning}
          sub={`${result.totalBillableHours} billable hrs`}
        />
        <ResultCard
          label="SE Tax Overhead"
          value={formatCurrency(result.seTaxOverhead)}
          color={colors.negative}
          sub="Extra vs W-2 FICA"
        />
      </div>

      {/* W-2 vs Freelancer Comparison */}
      <GlassCard>
        <h3 className="text-sm font-semibold text-body mb-4">W-2 Employee vs Freelancer</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-subtle">
                <th className="text-left py-2 text-muted font-medium">Category</th>
                <th className="text-right py-2 text-muted font-medium">W-2 Employee</th>
                <th className="text-right py-2 text-muted font-medium">Freelancer</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-subtle">
                <td className="py-2 text-body">Gross Income</td>
                <td className="py-2 text-right text-heading">{formatCurrency(targetW2Salary)}</td>
                <td className="py-2 text-right text-heading">{formatCurrency(result.annualGrossNeeded)}</td>
              </tr>
              <tr className="border-b border-subtle">
                <td className="py-2 text-body">Total Taxes</td>
                <td className="py-2 text-right text-heading">{formatCurrency(result.w2TotalTax)}</td>
                <td className="py-2 text-right text-heading">{formatCurrency(result.freelancerTotalTax)}</td>
              </tr>
              <tr className="border-b border-subtle">
                <td className="py-2 text-body">Health Insurance</td>
                <td className="py-2 text-right text-positive">Employer-provided</td>
                <td className="py-2 text-right text-negative">{formatCurrency(result.annualHealthInsurance)}/yr</td>
              </tr>
              <tr className="border-b border-subtle">
                <td className="py-2 text-body">PTO</td>
                <td className="py-2 text-right text-positive">Paid</td>
                <td className="py-2 text-right text-negative">Unpaid ({weeksPTO} wks)</td>
              </tr>
              <tr className="font-semibold">
                <td className="py-2 text-heading">Take-Home</td>
                <td className="py-2 text-right text-positive">{formatCurrency(result.w2TakeHome)}</td>
                <td className="py-2 text-right text-positive">{formatCurrency(result.freelancerTakeHome)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </GlassCard>

      {/* Waterfall Breakdown */}
      <GlassCard>
        <h3 className="text-sm font-semibold text-body mb-4">From W-2 Take-Home to Required Billing</h3>
        <div className="space-y-1">
          {result.waterfall.map((item, i) => {
            const isFirst = i === 0
            const color = isFirst ? colors.positive : colors.warning
            return (
              <div
                key={i}
                className={`flex items-center justify-between py-2 px-3 rounded-lg text-sm ${isFirst ? 'bg-positive-soft font-semibold' : ''}`}
              >
                <span className="text-heading">{item.label}</span>
                <span style={{ color }}>{isFirst ? '' : '+'}{formatCurrency(item.amount)}</span>
              </div>
            )
          })}
          <div className="flex items-center justify-between py-2 px-3 rounded-lg text-sm bg-primary-soft font-semibold">
            <span className="text-heading">Required Annual Billing</span>
            <span style={{ color: colors.primary }}>{formatCurrency(result.annualGrossNeeded)}</span>
          </div>
        </div>
      </GlassCard>

      {/* Tips */}
      <GlassCard className="!p-4" style={{ borderLeft: `3px solid ${colors.primary}` }}>
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${colors.primary}22`, color: colors.primary }}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-medium text-heading mb-1">Freelancer Tip</p>
            <p className="text-xs text-muted leading-relaxed">
              This rate ensures you match your W-2 take-home pay. In practice, consider adding 10-20% for:
              business expenses, accounting fees, liability insurance, equipment, retirement contributions (SEP-IRA/Solo 401k),
              and a buffer for slow months. Your effective rate should be closer
              to <strong className="text-heading">{formatCurrency(result.hourlyRate * 1.15)}/hr</strong>.
            </p>
          </div>
        </div>
      </GlassCard>
    </div>
  )
}
